import { ApiError } from 'api';
import { resetPasswordPath } from 'common/config/urls';
import { buildAuthSearchParams } from 'pages/Auth/utils';
import type { BuildResetPasswordUrlOptions, HttpLikeError } from './ResetPassword.types';

const PASSWORD_RESET_UID_PATTERN = /^[A-Za-z0-9_\-=]+$/;
const PASSWORD_RESET_TOKEN_PATTERN = /^[A-Za-z0-9]+-[A-Za-z0-9]+$/;

const getAuthActionStatus = (result: unknown): number | null => {
  if (result instanceof ApiError) {
    return result.status;
  }

  if (typeof result !== 'object' || result === null) {
    return null;
  }

  const httpLikeError = result as HttpLikeError;

  if (typeof httpLikeError.status === 'number') {
    return httpLikeError.status;
  }

  if (typeof httpLikeError.response?.status === 'number') {
    return httpLikeError.response.status;
  }

  return null;
};

const getErrorBody = (result: unknown): unknown => {
  if (result instanceof ApiError) {
    return result.body;
  }
  if (typeof result === 'object' && result !== null) {
    const httpLikeError = result as HttpLikeError;
    return httpLikeError.body ?? httpLikeError.response?.data;
  }
  return null;
};

export const isTurnstileTokenError = (result: unknown) => {
  const body = getErrorBody(result);

  return typeof body === 'object'
    && body !== null
    && Object.prototype.hasOwnProperty.call(body, 'turnstile_token');
};

export const getAuthActionErrorStatus = (result: unknown): number | null => {
  const status = getAuthActionStatus(result);

  if (status === null || status < 400) {
    return null;
  }

  return status;
};

export const getPasswordResetFailureMessage = (
  result: unknown,
  {
    laterError,
    submitError,
    turnstileError,
  }: {
    laterError: string;
    submitError: string;
    turnstileError: string;
  }
) => {
  if (isTurnstileTokenError(result)) {
    return turnstileError;
  }
  if (getAuthActionErrorStatus(result) !== null) {
    return submitError;
  }
  return laterError;
};

export const didAuthActionSucceed = (result: unknown) => {
  const status = getAuthActionStatus(result);

  // Some auth endpoints resolve with HTTP-like success payloads such as 204 instead of returning data.
  if (status !== null) {
    return status >= 200 && status < 300;
  }

  return !(result instanceof Error);
};

export const isAuthActionError = (result: unknown) => !didAuthActionSucceed(result);

export const isMalformedPasswordResetLink = (uid?: string, token?: string) =>
  !uid
  || !token
  || !PASSWORD_RESET_UID_PATTERN.test(uid)
  || !PASSWORD_RESET_TOKEN_PATTERN.test(token);

export const buildResetPasswordUrl = ({
  email,
  returnTo,
  source,
  view,
}: BuildResetPasswordUrlOptions = {}) => {
  const params = buildAuthSearchParams({
    email,
    returnTo,
    source,
    view,
  });
  const query = params.toString();

  return query ? `${resetPasswordPath}?${query}` : resetPasswordPath;
};
